// Where every page lives. One place builds the parent of each page from the registry, so the breadcrumb, the URL, the Back link and the sidebar highlight can never disagree.
//
// A REAL PAGE has a component. A cluster heading (Connections, Partner Management, System & Data, Account on the Settings launcher; a registry "group" such as Business Development
// when no page is named that) is NOT a page: it never appears in a breadcrumb or a URL. The parent of a page is:
//   1. an explicit override (a child with no list of its own, such as a team member's profile),
//   2. the page whose submenu lists it (HUNT lists Scout), or, when that submenu is only a heading, the page named after that heading's group (Settings),
//   3. for a page that is only reachable by a link, the page whose label is its registry group, if such a page exists.
// A hidden page with none of these gets Home as its parent, so it always has a way back; a listed page with none of these is top level.
import { MODULE_REGISTRY } from './moduleRegistry';

const OVERRIDES = {
  'email-contact': 'dashboard',
  'text-contact': 'dashboard',
  'crm-lookup': 'dashboard',
  'team-member-profile': 'team-members',
  lobby: 'lead-management', // retired page that redirects to Lead Management
  'voice-rules-preview': 'voice-rules', // tabs of the Voice Rules page
  'voice-rules-report': 'voice-rules',
  'voice-rules-wording': 'voice-rules',
  'reply-parser': 'email-settings', // tabs of the merged Email Settings page
  signatures: 'email-settings',
  'company-succession-moves': 'company-succession', // tabs of the merged Company Succession page
  'dead-domain-cleanup': 'company-succession',
  'data-health': 'system-health', // tabs of the merged System Health page
  'ops-audit': 'system-health',
  'app-errors': 'system-health',
  flagged: 'system-health', // the Flagged tab (leads people flagged)
  'import-manager': 'import-export', // tabs of the merged Import and Export page
  'export-manager': 'import-export',
  tracker: 'sales-log', // a tab of the Activity page
  'phoenix-pricebook': 'product-catalog', // a tab of the Catalog page
  quotes: 'quote-to-invoice', // the document tabs of the Sales Desk page
  'sales-orders': 'quote-to-invoice',
  'work-orders': 'quote-to-invoice',
  invoices: 'quote-to-invoice',
  'booking-settings': 'calendar-connections', // a section of Calendar Connections now
  'the-ledger': 'system-health',
};

function build(registry) {
  const entries = {}; // key -> { key, label, hasPage, parentKey?, topLevel }
  const pageByLabel = {};
  registry.forEach((m) => { if (m.component && !m.hidden) pageByLabel[m.label] = m; });
  registry.forEach((m) => {
    entries[m.key] = { key: m.key, label: m.label, hasPage: !!m.component || m.key === 'merger-history-report', top: m, heading: !m.component && !!m.subItems };
  });
  const parentOf = {};
  registry.forEach((m) => {
    (m.subItems || []).forEach((s) => {
      entries[s.key] = entries[s.key] || { key: s.key, label: s.label, hasPage: !!s.component, sub: s };
      // the heading (no page of its own) hands its children to the page named after its group
      const host = m.component ? m : (pageByLabel[m.group] || null);
      if (host && host.key !== s.key) parentOf[s.key] = host.key;
    });
  });
  registry.forEach((m) => {
    if (parentOf[m.key] || (m.subItems && !m.component)) return;
    if (m.hidden && m.group && pageByLabel[m.group] && pageByLabel[m.group].key !== m.key) parentOf[m.key] = pageByLabel[m.group].key;
    // a hidden page reached only by a link and named by no page gets Home as its way back (GenServ tools, Incompletes)
    else if (m.hidden && m.component && m.key !== 'dashboard') parentOf[m.key] = 'dashboard';
  });
  Object.entries(OVERRIDES).forEach(([k, p]) => { parentOf[k] = p; });
  return { entries, parentOf };
}

let cache = null;
const data = () => cache || (cache = build(MODULE_REGISTRY));

export const parentKey = (key) => data().parentOf[key] || null;
export const labelOf = (key) => (data().entries[key] || {}).label || key;

// Keys from the top-level page down to this one: ['settings', 'calendar-connections'].
export function chainOf(key) {
  const out = [key];
  const seen = new Set(out);
  let p = parentKey(key);
  while (p && !seen.has(p)) { out.unshift(p); seen.add(p); p = parentKey(p); }
  return out;
}

// The breadcrumb: real pages only, each clickable except the last.
export const crumbsOf = (key) => chainOf(key).map((k) => ({ key: k, label: labelOf(k) }));

// The URL for a page: #/settings/calendar-connections (the parents that are real pages, then the page).
export const hashFor = (key, query = '') => `#/${chainOf(key).join('/')}${query ? `?${query}` : ''}`;

// The page key in a URL: the LAST segment, so an old flat link (#/calendar-connections) and the hierarchical one both land on the same page.
// With `exists`, a last segment that is no longer a page (a retired or renamed page under a live parent) falls back to the nearest earlier segment that is, so a stale bookmark lands on its parent, not Home.
export function keyFromHash(hash, exists) {
  const path = String(hash || '').replace(/^#\/?/, '').split('?')[0];
  const segs = path.split('/').filter(Boolean).map((s) => { try { return decodeURIComponent(s); } catch (e) { return s; } });
  if (!segs.length) return '';
  if (exists) for (let i = segs.length - 1; i >= 0; i -= 1) if (exists(segs[i])) return segs[i];
  return segs[segs.length - 1];
}

// Which sidebar item to light up for a page: the page itself when the sidebar lists it, otherwise its nearest ancestor that the sidebar lists (a Settings page keeps Settings selected).
export function sidebarKeyFor(key, listed) {
  const chain = chainOf(key);
  for (let i = chain.length - 1; i >= 0; i -= 1) if (listed(chain[i])) return chain[i];
  return key;
}

// For the audit and the tests: every registered page key, whether it has a page and what its parent is.
export const allPageKeys = () => Object.values(data().entries).filter((e) => e.hasPage).map((e) => e.key);
export const resetNavigationCache = () => { cache = null; };
